/**
 * Membership helper functions.
 * Lightweight, reusable utilities for membership tier checks and ad discount calculation.
 */

import { createClient } from '@/lib/supabase/client';

export type MembershipTier = 'business' | 'premium' | null;
export type MembershipStatus = 'active' | 'inactive';

export interface UserMembership {
  tier: MembershipTier;
  status: MembershipStatus;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
}

/** Discount rates per tier (as decimal fractions) */
export const MEMBERSHIP_DISCOUNTS: Record<string, { min: number; max: number; label: string }> = {
  business: { min: 0.10, max: 0.15, label: '10–15%' },
  premium:  { min: 0.20, max: 0.30, label: '20–30%' },
};

/** Display names for tiers */
export const MEMBERSHIP_TIER_LABELS: Record<string, string> = {
  business: 'Business Member',
  premium:  'Premium Growth Member',
};

/**
 * Fetch the membership info for a given user from Supabase.
 * Returns null fields if the user has no membership.
 */
export async function getUserMembership(userId: string): Promise<UserMembership> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('membership_tier, membership_status, membership_start_date, membership_end_date')
    .eq('id', userId)
    .maybeSingle();

  if (error || !data) {
    return { tier: null, status: 'inactive', startDate: null, endDate: null, isActive: false };
  }

  const tier = (data.membership_tier as MembershipTier) ?? null;
  const status = (data.membership_status as MembershipStatus) ?? 'inactive';
  const isActive = tier !== null && status === 'active';

  return {
    tier,
    status,
    startDate: data.membership_start_date ?? null,
    endDate: data.membership_end_date ?? null,
    isActive,
  };
}

/**
 * Calculate the discounted price for an ad placement based on membership tier.
 * Uses the midpoint of the discount range for a clean, predictable discount.
 *
 * @param membership - The user's membership info
 * @param basePrice  - The base price in dollars (not cents)
 * @returns { discountRate, discountAmount, finalPrice, discountLabel }
 */
export function calculateAdDiscount(
  membership: UserMembership,
  basePrice: number
): {
  discountRate: number;
  discountAmount: number;
  finalPrice: number;
  discountLabel: string;
} {
  if (!membership.isActive || !membership.tier) {
    return { discountRate: 0, discountAmount: 0, finalPrice: basePrice, discountLabel: '' };
  }

  const config = MEMBERSHIP_DISCOUNTS[membership.tier];
  if (!config) {
    return { discountRate: 0, discountAmount: 0, finalPrice: basePrice, discountLabel: '' };
  }

  // Use midpoint of range for consistent discount
  const discountRate = (config.min + config.max) / 2;
  const discountAmount = Math.round(basePrice * discountRate);
  const finalPrice = basePrice - discountAmount;
  const tierLabel = MEMBERSHIP_TIER_LABELS[membership.tier] ?? membership.tier;
  const discountLabel = `${tierLabel} discount (${config.label})`;

  return { discountRate, discountAmount, finalPrice, discountLabel };
}

/**
 * Upgrade a user's membership tier in Supabase.
 * Called from the Membership & Benefits dashboard section only.
 */
export async function upgradeMembership(
  userId: string,
  tier: 'business' | 'premium'
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const now = new Date().toISOString();
  const oneYearLater = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString();

  const { error } = await supabase
    .from('user_profiles')
    .update({
      membership_tier: tier,
      membership_status: 'active',
      membership_start_date: now,
      membership_end_date: oneYearLater,
    })
    .eq('id', userId);

  if (error) {
    return { success: false, error: error.message };
  }
  return { success: true };
}
